import { userLogout } from '@redux/authent/authent.reducer';
import { createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';

import { HoloLog } from '@/types/c2/logs.type';
import { NotificationType } from '@/types/data/data.types';
import { SubCameraConfiguration } from '@/types/sensor/sensors';
import { PartialDrone } from '@/types/utm/drone.types';

import { logsAdapter, notificationAdapter } from './global.adapters';

export type ControlledCamera = { cameraUniqueKey: string | null; subCameraId: number | undefined };

export type GlobalState = {
  selectedCameraKey: string | null;
  joystickControlId: string | null;
  joystickControlledCam: ControlledCamera;
  selectedDoubtCheckSegmentName: string | null;
  selectedDoubtCheckPerimeterCameraUniqueKey: string | null;
  notificationQueue: EntityState<NotificationType, string>;
  selectedCameraStreams: SubCameraConfiguration[];
  newLogBookEntry: boolean;
  logs: EntityState<HoloLog, string>;
  isInactiveAt: Date | null;
  currentWindowId: string;
  selectedNotificationId: string | null;
  selectedDrone: PartialDrone | null;
  selectedMonitoringSensorUniqueKey: string | null;
};

export const GLOBAL_INITIAL_STATE: GlobalState = {
  selectedCameraKey: null,
  joystickControlId: null,
  joystickControlledCam: { cameraUniqueKey: null, subCameraId: undefined },
  selectedDoubtCheckSegmentName: null,
  selectedDoubtCheckPerimeterCameraUniqueKey: null,
  notificationQueue: notificationAdapter.getInitialState(),
  selectedCameraStreams: [],
  newLogBookEntry: false,
  logs: logsAdapter.getInitialState(),
  isInactiveAt: null,
  currentWindowId: '',
  selectedNotificationId: null,
  selectedDrone: null,
  selectedMonitoringSensorUniqueKey: null,
};

const global = createSlice({
  name: 'global',
  initialState: GLOBAL_INITIAL_STATE,
  reducers: {
    updateSelectedCameraKey: (state, action: PayloadAction<string | null>) => {
      state.selectedCameraKey = action.payload;
    },
    updateJoystickControlId: (state, action: PayloadAction<string | null>) => {
      state.joystickControlId = action.payload;
      if (action.payload === null) {
        state.joystickControlledCam = { cameraUniqueKey: null, subCameraId: undefined };
      }
    },
    updateJoystickControlledCam: (state, action: PayloadAction<ControlledCamera>) => {
      state.joystickControlledCam = action.payload;
    },
    updateSelectedDoubtCheckSegmentName: (state, action: PayloadAction<string | null>) => {
      state.selectedDoubtCheckSegmentName = action.payload;
      state.selectedDoubtCheckPerimeterCameraUniqueKey = null;
    },
    updateSelectedDoubtCheckPerimeterCameraUniqueKey: (state, action: PayloadAction<string | null>) => {
      state.selectedDoubtCheckPerimeterCameraUniqueKey = action.payload;
      state.selectedDoubtCheckSegmentName = null;
    },
    addNotificationToQueue: ({ notificationQueue }, action: PayloadAction<NotificationType>) => {
      notificationAdapter.addOne(notificationQueue, action.payload);
    },
    deleteNotificationFromQueue: ({ notificationQueue }, action: PayloadAction<NotificationType>) => {
      notificationAdapter.removeOne(notificationQueue, action.payload.id);
    },
    updateNewLogBookEntry(state, action: PayloadAction<boolean>) {
      state.newLogBookEntry = action.payload;
    },
    addSelectedCameraStreams(state, action: PayloadAction<SubCameraConfiguration>) {
      state.selectedCameraStreams = [...state.selectedCameraStreams, action.payload];
    },
    removeSelectedCameraStreams(state, action: PayloadAction<SubCameraConfiguration>) {
      const index = state.selectedCameraStreams.findIndex((camera) => camera.id === action.payload.id);
      if (index > -1) {
        state.selectedCameraStreams = state.selectedCameraStreams.toSpliced(index, 1);
      }
    },
    updateLogs(state, action: PayloadAction<HoloLog[]>) {
      state.logs = logsAdapter.upsertMany(state.logs, action.payload);
    },
    addLog: ({ logs }, action: PayloadAction<HoloLog>) => {
      logsAdapter.addOne(logs, action.payload);
    },
    updateLog: ({ logs }, action: PayloadAction<HoloLog>) => {
      logsAdapter.upsertOne(logs, action.payload);
    },
    deleteLog: ({ logs }, action: PayloadAction<HoloLog>) => {
      logsAdapter.removeOne(logs, action.payload.code);
    },
    updateIsInactiveAt: (state, action: PayloadAction<Date | null>) => {
      state.isInactiveAt = action.payload;
    },
    updateCurrentWindowId: (state, action: PayloadAction<string>) => {
      state.currentWindowId = action.payload;
    },
    updateSelectedNotificationId: (state, action: PayloadAction<string | null>) => {
      state.selectedNotificationId = action.payload;
    },
    updateSelectedDrone: (state, action: PayloadAction<PartialDrone | null>) => {
      state.selectedDrone = action.payload;
    },
    updateSelectedMonitoringSensorUniqueKey: (state, action: PayloadAction<string | null>) => {
      state.selectedMonitoringSensorUniqueKey = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userLogout, () => GLOBAL_INITIAL_STATE);
  },
});

export const {
  updateSelectedCameraKey,
  updateJoystickControlId,
  updateSelectedDoubtCheckSegmentName,
  updateSelectedDoubtCheckPerimeterCameraUniqueKey,
  updateJoystickControlledCam,
  addNotificationToQueue,
  deleteNotificationFromQueue,
  updateNewLogBookEntry,
  addSelectedCameraStreams,
  removeSelectedCameraStreams,
  updateLogs,
  addLog,
  deleteLog,
  updateLog,
  updateIsInactiveAt,
  updateCurrentWindowId,
  updateSelectedNotificationId,
  updateSelectedDrone,
  updateSelectedMonitoringSensorUniqueKey,
} = global.actions;
export default global.reducer;
